import { useCallback, useEffect, useMemo, useRef, useState, ComponentType, ReactNode } from 'react'
import { connect } from 'react-redux'
import { AnimatePresence, motion } from 'framer-motion'
import { Filter, ChevronDown, ChevronUp, ArrowUpRight, X } from 'lucide-react'

import {
  FilterDropdown,
  FilterDropdownItem,
  FilterFieldButton,
  FilterPanel,
  FilterSection,
  FilterField,
} from '../common/FilterPanel.tsx'
import DualRangeSlider from '../common/DualRangeSlider.tsx'
import styles from './FilterQuotation.module.scss'
import { filterQuotations, getSubquotationType } from '../../../store/actions'
import { getCategories } from '../../../store/actions/categoryActions'
import { JURISDICTIONS_WITH_ALL } from '../../../constants/jurisdictions.ts'

const Presence = AnimatePresence as unknown as ComponentType<{ children: ReactNode; initial?: boolean }>

type Option = {
  id: number
  name: string
}

type FilterQuotationProps = {
  filterQuotations: (filter: any, page: number, pageSize: number) => void
  setIsFiltered: (value: boolean) => void
  setIsAdjudicated?: (value: boolean) => void
  getSubquotationType: () => void
  getCategories: () => void
  listaDeRubros?: Option[]
  resultados: number
  quotationCount: number
  currentPage: number
  setCurrentPage: (page: number) => void
  setSelectedOption: (value: string) => void
  selectedOption: 'Publicas' | 'Privadas'
}

const DEFAULT_PAGE_SIZE = 9

const currencyOptions = ['Pesos', 'Dólares'] as const
const sortOptions = ['Recientes', 'Antiguos'] as const

const FilterQuotation = ({
  filterQuotations: dispatchFilter,
  setIsFiltered,
  setIsAdjudicated,
  listaDeRubros = [],
  getSubquotationType: fetchSubquotationType,
  getCategories: fetchCategories,
  resultados,
  quotationCount,
  currentPage,
  setCurrentPage,
  setSelectedOption,
  selectedOption,
}: FilterQuotationProps) => {
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [selectedOptionOrganism, setSelectedOptionOrganism] = useState<Option>({ id: 0, name: 'Todos' })
  const [selectedOptionJurisdiction, setSelectedOptionJurisdiction] = useState<Option>({ id: 0, name: 'Todas' })
  const [selectedWorkType, setSelectedWorkType] = useState<Option>({ id: 0, name: 'Todas' })
  const [selectedCurrency, setSelectedCurrency] = useState<typeof currencyOptions[number]>('Pesos')
  const [minAmount, setMinAmount] = useState(0)
  const [maxAmount, setMaxAmount] = useState(0)
  const [showExactDateInput, setShowExactDateInput] = useState(false)
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [selectedDateRange, setSelectedDateRange] = useState<string | null>(null)
  const [asociatedWords, setAsociatedWords] = useState('')
  const [selectedSort, setSelectedSort] = useState<typeof sortOptions[number]>('Recientes')
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false)
  const [mobileAdvancedOpen, setMobileAdvancedOpen] = useState(false)

  const mobileFilterRef = useRef<HTMLDivElement | null>(null)

  const isPublicQuote = selectedOption === 'Publicas'

  const getActiveFiltersCount = useCallback(() => {
    let count = 0
    if (isPublicQuote && selectedOptionOrganism.id !== 0) count++
    if (selectedOptionJurisdiction.id !== 0) count++
    if (selectedWorkType.id !== 0) count++
    if (selectedCurrency !== 'Pesos') count++
    if (minAmount > 0 || maxAmount > 0) count++
    if (selectedDateRange || selectedDate) count++
    if (asociatedWords.trim() !== '') count++
    if (selectedSort !== 'Recientes') count++
    return count
  }, [
    isPublicQuote,
    selectedOptionOrganism.id,
    selectedOptionJurisdiction.id,
    selectedWorkType.id,
    selectedCurrency,
    minAmount,
    maxAmount,
    selectedDateRange,
    selectedDate,
    asociatedWords,
    selectedSort,
  ])

  const filterCount = useMemo(() => getActiveFiltersCount(), [getActiveFiltersCount])

  const currencyFormatter = useMemo(() => {
    const currency = selectedCurrency === 'Dólares' ? 'USD' : 'ARS'
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency, minimumFractionDigits: 0 })
  }, [selectedCurrency])

  const amountLimit = selectedCurrency === 'Pesos' ? 500_000_000 : 50_000_000

  const formatDate = (date: string) => {
    const utcDate = new Date(date)
    const day = String(utcDate.getUTCDate()).padStart(2, '0')
    const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0')
    const year = utcDate.getUTCFullYear()
    return `${day}/${month}/${year}`
  }

  const handleFilterClick = (filterName: string) => {
    setSelectedFilter((prev) => (prev === filterName ? null : filterName))
  }

  const handleMobileOpen = () => {
    setMobileFilterOpen(true)
    document.body.style.overflow = 'hidden'
  }

  const handleMobileClose = () => {
    setMobileFilterOpen(false)
    setMobileAdvancedOpen(false)
    document.body.style.overflow = ''
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mobileFilterRef.current && !mobileFilterRef.current.contains(event.target as Node)) {
        handleMobileClose()
      }
    }

    if (mobileFilterOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [mobileFilterOpen])

  useEffect(() => {
    fetchCategories()
    fetchSubquotationType()
  }, [fetchCategories, fetchSubquotationType])

  const generateDateRange = (dateRange: string | null) => {
    if (!dateRange) return null

    const today = new Date()
    const addDays = (value: number) => {
      const next = new Date(today)
      next.setDate(today.getDate() + value)
      return next
    }

    switch (dateRange) {
      case 'Próximos 30 días':
        return [today, addDays(30)]
      case '30 a 60 días':
        return [addDays(30), addDays(60)]
      case '60 a 90 días':
        return [addDays(60), addDays(90)]
      default:
        return null
    }
  }

  const buildFilterPayload = useCallback(() => {
    const toISO = (date: Date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    const dateRange = generateDateRange(selectedDateRange)
    const dateFrom = selectedDate ? toISO(new Date()) : dateRange ? toISO(dateRange[0]) : null
    const dateTo = selectedDate ? selectedDate : dateRange ? toISO(dateRange[1]) : null

    return {
      sort: selectedSort === 'Recientes' ? 'DESC' : 'ASC',
      asociatedWords,
      zone: null,
      minAmount: minAmount || null,
      maxAmount: maxAmount || null,
      currency: selectedCurrency === 'Dólares' ? 'USD' : 'ARS',
      jurisdiction: selectedOptionJurisdiction.id !== 0 ? selectedOptionJurisdiction.id : null,
      organism: selectedOptionOrganism.id !== 0 ? selectedOptionOrganism.id : null,
      workType: selectedWorkType.id !== 0 ? selectedWorkType.id : null,
      dateRange: { dateFrom, dateTo },
      projectType: selectedOption,
    }
  }, [
    asociatedWords,
    maxAmount,
    minAmount,
    selectedCurrency,
    selectedDate,
    selectedDateRange,
    selectedOption,
    selectedOptionJurisdiction.id,
    selectedOptionOrganism.id,
    selectedSort,
    selectedWorkType.id,
  ])

  const markAdjudicated = useCallback(
    (value: boolean) => {
      if (typeof setIsAdjudicated === 'function') {
        setIsAdjudicated(value)
      }
    },
    [setIsAdjudicated],
  )

  const filterKey = useMemo(
    () =>
      JSON.stringify({
        selectedOption,
        selectedOptionOrganism: selectedOptionOrganism.id,
        selectedOptionJurisdiction: selectedOptionJurisdiction.id,
        selectedWorkType: selectedWorkType.id,
        selectedCurrency,
        minAmount,
        maxAmount,
        selectedDate,
        selectedDateRange,
        asociatedWords,
        selectedSort,
      }),
    [
      selectedOption,
      selectedOptionOrganism.id,
      selectedOptionJurisdiction.id,
      selectedWorkType.id,
      selectedCurrency,
      minAmount,
      maxAmount,
      selectedDate,
      selectedDateRange,
      asociatedWords,
      selectedSort,
    ],
  )
  const prevFilterKey = useRef<string | null>(null)

  const handleFilter = useCallback(
    (hasFilter: boolean) => {
      setIsFiltered(hasFilter)
      const payload = buildFilterPayload()
      const filterChanged = prevFilterKey.current !== null && prevFilterKey.current !== filterKey
      prevFilterKey.current = filterKey
      if (filterChanged) {
        setCurrentPage(1)
        dispatchFilter(payload, 1, DEFAULT_PAGE_SIZE)
      } else {
        dispatchFilter(payload, currentPage, DEFAULT_PAGE_SIZE)
      }
    },
    [buildFilterPayload, currentPage, dispatchFilter, filterKey, setIsFiltered, setCurrentPage],
  )

  useEffect(() => {
    handleFilter(true)
  }, [handleFilter])

  const handleOptionSelect = (option: 'Publicas' | 'Privadas') => {
    setSelectedOption(option)
    setSelectedFilter(null)
    setSelectedWorkType({ id: 0, name: 'Todas' })
    if (option === 'Privadas') {
      setSelectedOptionOrganism({ id: 0, name: 'Todos' })
    }
    markAdjudicated(option === 'Privadas')
    setCurrentPage(1)
  }

  const handleOrganismSelect = (option: Option) => {
    setSelectedOptionOrganism(option)
    setSelectedFilter(null)
    setCurrentPage(1)
  }

  const handleJurisdictionSelect = (option: Option) => {
    setSelectedOptionJurisdiction(option)
    setSelectedFilter(null)
    setCurrentPage(1)
  }

  const handleWorkTypeSelect = (option: Option) => {
    setSelectedWorkType(option)
    setSelectedFilter(null)
    setCurrentPage(1)
  }

  const handleReset = () => {
    setSelectedOption('Publicas')
    setSelectedOptionOrganism({ id: 0, name: 'Todos' })
    setSelectedOptionJurisdiction({ id: 0, name: 'Todas' })
    setSelectedWorkType({ id: 0, name: 'Todas' })
    setSelectedCurrency('Pesos')
    setMinAmount(0)
    setMaxAmount(0)
    setSelectedDate(null)
    setSelectedDateRange(null)
    setAsociatedWords('')
    setSelectedSort('Recientes')
    setSelectedFilter(null)
    setIsFiltered(false)
    markAdjudicated(false)
    setCurrentPage(1)
    setMobileAdvancedOpen(false)
  }

  const renderDropdown = (items: Option[], onSelect: (option: Option) => void, activeId: number) => (
    <FilterDropdown>
      {items.map((item) => (
        <FilterDropdownItem key={item.id} onSelect={() => onSelect(item)} active={item.id === activeId}>
          {item.name}
        </FilterDropdownItem>
      ))}
    </FilterDropdown>
  )

  const summary = (
    <div className={styles.summaryRow}>
      <p className={styles.summaryText}>
        {resultados === 0 ? (
          'No se encontraron resultados'
        ) : resultados === 1 ? (
          <>
            Se encontró <span className={styles.summaryHighlight}>1</span> resultado
          </>
        ) : (
          <>
            Se encontraron <span className={styles.summaryHighlight}>{resultados}</span> resultados
          </>
        )}
        {resultados > DEFAULT_PAGE_SIZE && (
          <span className={styles.summaryExtra}>
            · Página {currentPage} de {Math.ceil(resultados / DEFAULT_PAGE_SIZE)}
          </span>
        )}
      </p>
      <div className={styles.sortControl}>
        <span className={styles.sortLabel}>Ordenar por</span>
        <button
          type="button"
          className={styles.sortButton}
          onClick={() => setSelectedSort((prev) => (prev === 'Recientes' ? 'Antiguos' : 'Recientes'))}
        >
          {selectedSort}
          <ChevronDown size={16} />
        </button>
      </div>
    </div>
  )

  const primary = (
    <>
      <FilterSection>
        <div className={styles.toggleGroup}>
          <button
            type="button"
            className={styles.toggle}
            data-active={isPublicQuote}
            onClick={() => handleOptionSelect('Publicas')}
          >
            Públicas
          </button>
          <button
            type="button"
            className={styles.toggle}
            data-active={!isPublicQuote}
            onClick={() => handleOptionSelect('Privadas')}
          >
            Privadas
          </button>
        </div>
      </FilterSection>

      {isPublicQuote && (
        <FilterSection>
          <FilterField>
            <FilterFieldButton
              title="Organismo"
              value={selectedOptionOrganism.name}
              onClick={() => handleFilterClick('organism')}
              active={selectedFilter === 'organism'}
            />
            {selectedFilter === 'organism' &&
              renderDropdown(ORGANISM_OPTIONS, handleOrganismSelect, selectedOptionOrganism.id)}
          </FilterField>
        </FilterSection>
      )}

      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title="Jurisdicción"
            value={selectedOptionJurisdiction.name}
            onClick={() => handleFilterClick('jurisdiction')}
            active={selectedFilter === 'jurisdiction'}
          />
          {selectedFilter === 'jurisdiction' &&
            renderDropdown(JURISDICTION_OPTIONS, handleJurisdictionSelect, selectedOptionJurisdiction.id)}
        </FilterField>
      </FilterSection>

      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title={isPublicQuote ? 'Tipo de obra' : 'Material'}
            value={selectedWorkType.name}
            onClick={() => handleFilterClick('workType')}
            active={selectedFilter === 'workType'}
          />
          {selectedFilter === 'workType' && renderDropdown(
            isPublicQuote
              ? PUBLIC_WORK_TYPES
              : listaDeRubros.map((item) => ({ id: Number(item.id), name: item.name })),
            handleWorkTypeSelect,
            selectedWorkType.id,
          )}
        </FilterField>
      </FilterSection>
    </>
  )

  const advanced = (
    <>
      <FilterSection>
        <div className={styles.pillGroup}>
          {currencyOptions.map((option) => (
            <button
              key={option}
              type="button"
              className={styles.pill}
              data-active={option === selectedCurrency}
              onClick={() => setSelectedCurrency(option)}
            >
              {option}
            </button>
          ))}
        </div>
      </FilterSection>

      <FilterSection>
        <div className={styles.rangeControl}>
          <DualRangeSlider
            min={0}
            max={amountLimit}
            step={selectedCurrency === 'Pesos' ? 1_000_000 : 1_000}
            values={[minAmount, maxAmount === 0 ? amountLimit : maxAmount]}
            onChange={([nextMin, nextMax]) => {
              setMinAmount(nextMin)
              setMaxAmount(nextMax === amountLimit ? 0 : nextMax)
            }}
            formatLabels={([currentMin, currentMax]) => {
              const minLabel = currentMin === 0 ? 'Sin mínimo' : currencyFormatter.format(currentMin)
              const maxLabel = maxAmount === 0 ? 'Sin máximo' : currencyFormatter.format(currentMax)
              return [minLabel, maxLabel]
            }}
            presets={
              selectedCurrency === 'Pesos'
                ? [
                    { label: 'Hasta 20M', values: [0, 20_000_000] },
                    { label: '20M a 80M', values: [20_000_000, 80_000_000] },
                    { label: '80M a 200M', values: [80_000_000, 200_000_000] },
                    { label: '200M+', values: [200_000_000, amountLimit] },
                  ]
                : [
                    { label: 'Hasta 2M', values: [0, 2_000_000] },
                    { label: '2M a 8M', values: [2_000_000, 8_000_000] },
                    { label: '8M a 20M', values: [8_000_000, 20_000_000] },
                    { label: '20M+', values: [20_000_000, amountLimit] },
                  ]
            }
          />
        </div>
      </FilterSection>

      <FilterSection>
        <FilterField>
          <FilterFieldButton
            title="Rango"
            value={selectedDateRange || (selectedDate ? formatDate(selectedDate) : undefined)}
            placeholder="Elegir"
            onClick={() => handleFilterClick('date')}
            active={selectedFilter === 'date'}
          />
          {selectedFilter === 'date' && (
            <FilterDropdown>
              {['Próximos 30 días', '30 a 60 días', '60 a 90 días'].map((label) => (
                <FilterDropdownItem
                  key={label}
                  onSelect={() => {
                    setSelectedDateRange(label)
                    setSelectedDate(null)
                    setShowExactDateInput(false)
                    setSelectedFilter(null)
                  }}
                  active={selectedDateRange === label}
                >
                  {label}
                </FilterDropdownItem>
              ))}
              <div className={styles.dropdownFooter}>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() => {
                    setSelectedDateRange(null)
                    setShowExactDateInput(true)
                  }}
                >
                  Ingresar fecha exacta
                </button>
                {showExactDateInput && (
                  <input
                    type="date"
                    className={styles.dateInput}
                    value={selectedDate ?? ''}
                    onChange={(event) => setSelectedDate(event.target.value)}
                  />
                )}
              </div>
            </FilterDropdown>
          )}
        </FilterField>
      </FilterSection>

      <FilterSection>
        <input
          type="text"
          className={styles.textInput}
          value={asociatedWords}
          placeholder="Ej. Hormigón, movimiento de suelos..."
          onChange={(event) => setAsociatedWords(event.target.value)}
        />
      </FilterSection>
    </>
  )

  const renderDesktop = () => (
    <FilterPanel
      title="Cotización de materiales"
      filterCount={filterCount}
      onReset={handleReset}
      onToggleAdvanced={() => setAdvancedOpen((open) => !open)}
      advancedOpen={advancedOpen}
      summary={summary}
      primary={primary}
      advanced={advanced}
    />
  )

  const renderMobile = () => (
    <>
      <div className={styles.mobileStickyControls}>
        <button type="button" className={styles.mobileTrigger} onClick={handleMobileOpen}>
          <Filter size={18} />
          Filtros
          {filterCount > 0 && <span className={styles.mobileBadge}>{filterCount}</span>}
        </button>
        <button type="button" className={styles.mobileSortButton} onClick={() => setSelectedSort((prev) => (prev === 'Recientes' ? 'Antiguos' : 'Recientes'))}>
          {selectedSort}
          <ChevronDown size={16} />
        </button>
      </div>

      <Presence initial={false}>
        {mobileFilterOpen && (
          <motion.div
            className={styles.mobileOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              ref={mobileFilterRef}
              className={styles.mobileSheet}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
            >
              <header className={styles.mobileHeader}>
                <div>
                  <h3>Filtros</h3>
                  <p>{resultados} resultados</p>
                </div>
                <button type="button" className={styles.mobileClose} onClick={handleMobileClose}>
                  <X size={20} />
                </button>
              </header>

              <div className={styles.mobileContent}>
                <div className={styles.mobileSegmented}>
                  <button
                    type="button"
                    className={styles.segmented}
                    data-active={isPublicQuote}
                    onClick={() => handleOptionSelect('Publicas')}
                  >
                    Públicas
                  </button>
                  <button
                    type="button"
                    className={styles.segmented}
                    data-active={!isPublicQuote}
                    onClick={() => handleOptionSelect('Privadas')}
                  >
                    Privadas
                  </button>
                </div>

                <div className={styles.mobileFields}>
                  {isPublicQuote && (
                    <>
                      <FilterFieldButton
                        title="Organismo"
                        value={selectedOptionOrganism.name}
                        onClick={() => handleFilterClick('organism-mobile')}
                        active={selectedFilter === 'organism-mobile'}
                      />
                      <Presence initial={false}>
                        {selectedFilter === 'organism-mobile' && (
                          <motion.div
                            key="organism-mobile-dropdown"
                            className={styles.mobileDropdown}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.24, ease: 'easeOut' }}
                            style={{ overflow: 'hidden' }}
                          >
                            {ORGANISM_OPTIONS.map((option) => (
                              <FilterDropdownItem
                                key={option.id}
                                onSelect={() => {
                                  handleOrganismSelect(option)
                                  setSelectedFilter(null)
                                }}
                                active={selectedOptionOrganism.id === option.id}
                              >
                                {option.name}
                              </FilterDropdownItem>
                            ))}
                          </motion.div>
                        )}
                      </Presence>
                    </>
                  )}

                  <FilterFieldButton
                    title="Jurisdicción"
                    value={selectedOptionJurisdiction.name}
                    onClick={() => handleFilterClick('jurisdiction-mobile')}
                    active={selectedFilter === 'jurisdiction-mobile'}
                  />
                  <Presence initial={false}>
                    {selectedFilter === 'jurisdiction-mobile' && (
                      <motion.div
                        key="jurisdiction-mobile-dropdown"
                        className={styles.mobileDropdown}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.24, ease: 'easeOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        {JURISDICTION_OPTIONS.map((option) => (
                          <FilterDropdownItem
                            key={option.id}
                            onSelect={() => {
                              handleJurisdictionSelect(option)
                              setSelectedFilter(null)
                            }}
                            active={selectedOptionJurisdiction.id === option.id}
                          >
                            {option.name}
                          </FilterDropdownItem>
                        ))}
                      </motion.div>
                    )}
                  </Presence>

                  <FilterFieldButton
                    title={isPublicQuote ? 'Tipo de obra' : 'Material'}
                    value={selectedWorkType.name}
                    onClick={() => handleFilterClick('workType-mobile')}
                    active={selectedFilter === 'workType-mobile'}
                  />
                  <Presence initial={false}>
                    {selectedFilter === 'workType-mobile' && (
                      <motion.div
                        key="worktype-mobile-dropdown"
                        className={styles.mobileDropdown}
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.24, ease: 'easeOut' }}
                        style={{ overflow: 'hidden' }}
                      >
                        {listaDeRubros.map((item) => ({ id: Number(item.id), name: item.name })).map((option) => (
                          <FilterDropdownItem
                            key={option.id}
                            onSelect={() => {
                              handleWorkTypeSelect(option)
                              setSelectedFilter(null)
                            }}
                            active={selectedWorkType.id === option.id}
                          >
                            {option.name}
                          </FilterDropdownItem>
                        ))}
                      </motion.div>
                    )}
                  </Presence>
                </div>

                <button type="button" className={styles.mobileAdvancedToggle} onClick={() => setMobileAdvancedOpen((open) => !open)}>
                  Búsqueda avanzada
                  {mobileAdvancedOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                <Presence initial={false}>
                  {mobileAdvancedOpen && (
                    <motion.div
                      className={styles.mobileAdvanced}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <div className={styles.mobilePills}>
                        {currencyOptions.map((option) => (
                          <button
                            key={option}
                            type="button"
                            className={styles.pill}
                            data-active={option === selectedCurrency}
                            onClick={() => setSelectedCurrency(option)}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                      <div className={styles.mobileRangeControl}>
                        <DualRangeSlider
                          min={0}
                          max={amountLimit}
                          step={selectedCurrency === 'Pesos' ? 1_000_000 : 1_000}
                          values={[minAmount, maxAmount === 0 ? amountLimit : maxAmount]}
                          onChange={([nextMin, nextMax]) => {
                            setMinAmount(nextMin)
                            setMaxAmount(nextMax === amountLimit ? 0 : nextMax)
                          }}
                          formatLabels={([currentMin, currentMax]) => {
                            const minLabel = currentMin === 0 ? 'Sin mínimo' : currencyFormatter.format(currentMin)
                            const maxLabel = maxAmount === 0 ? 'Sin máximo' : currencyFormatter.format(currentMax)
                            return [minLabel, maxLabel]
                          }}
                          presets={[]}
                        />
                      </div>
                      <FilterFieldButton
                        title="Rango"
                        value={selectedDateRange || (selectedDate ? formatDate(selectedDate) : undefined)}
                        placeholder="Elegir"
                        onClick={() => handleFilterClick('date-mobile')}
                        active={selectedFilter === 'date-mobile'}
                      />
                      <Presence initial={false}>
                        {selectedFilter === 'date-mobile' && (
                          <motion.div
                            key="date-mobile-dropdown"
                            className={styles.mobileDropdown}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.24, ease: 'easeOut' }}
                            style={{ overflow: 'hidden' }}
                          >
                            {['Próximos 30 días', '30 a 60 días', '60 a 90 días'].map((label) => (
                              <FilterDropdownItem
                                key={label}
                                onSelect={() => {
                                  setSelectedDateRange(label)
                                  setSelectedDate(null)
                                  setShowExactDateInput(false)
                                }}
                                active={selectedDateRange === label}
                              >
                                {label}
                              </FilterDropdownItem>
                            ))}
                            <div className={styles.dropdownFooter}>
                              <button
                                type="button"
                                className={styles.linkButton}
                                onClick={() => {
                                  setSelectedDateRange(null)
                                  setShowExactDateInput(true)
                                }}
                              >
                                Ingresar fecha exacta
                              </button>
                              {showExactDateInput && (
                                <input
                                  type="date"
                                  className={styles.dateInput}
                                  value={selectedDate ?? ''}
                                  onChange={(event) => setSelectedDate(event.target.value)}
                                />
                              )}
                            </div>
                          </motion.div>
                        )}
                      </Presence>
                      <input
                        type="text"
                        className={styles.textInput}
                        value={asociatedWords}
                        placeholder="Palabras clave"
                        onChange={(event) => setAsociatedWords(event.target.value)}
                      />
                    </motion.div>
                  )}
                </Presence>
              </div>

              <footer className={styles.mobileFooter}>
                <button type="button" className={styles.mobileReset} onClick={handleReset}>
                  Limpiar filtros
                </button>
              </footer>
            </motion.div>
          </motion.div>
        )}
      </Presence>
    </>
  )

  return (
    <div className={styles.root}>
      <div className={styles.desktop}>{renderDesktop()}</div>
      <div className={styles.mobile}>{renderMobile()}</div>
    </div>
  )
}

const mapStateToProps = (state: any) => {
  const list = state.category.categoryList
  const listaDeRubros = Array.isArray(list) ? list : list?.data ?? []
  return {
    resultados: state.profile.itemCount,
    quotationCount: state.profile.quotations?.entities?.length ?? 0,
    listaDeRubros,
  }
}

const mapDispatchToProps = {
  filterQuotations,
  getSubquotationType,
  getCategories,
}

const JURISDICTION_OPTIONS: Option[] = JURISDICTIONS_WITH_ALL as Option[]

const ORGANISM_OPTIONS: Option[] = [
  { id: 0, name: 'Todos' },
  { id: 1, name: 'Nacional' },
  { id: 2, name: 'Provincial' },
  { id: 3, name: 'Municipal' },
  { id: 4, name: 'Empresa Pública' },
]

const PUBLIC_WORK_TYPES: Option[] = [
  { id: 1, name: 'Obras Viales' },
  { id: 2, name: 'Obras eléctricas' },
  { id: 3, name: 'Obras de gas' },
  { id: 4, name: 'Obras Sanitarias / Saneamiento' },
  { id: 5, name: 'Obras de Arquitectura (Nuevas)' },
  { id: 6, name: 'Obras de Arquitectura (Remodelaciones)' },
  { id: 7, name: 'Servicios profesionales' },
  { id: 8, name: 'Servicios de consultoría' },
  { id: 9, name: 'Servicios varios' },
  { id: 10, name: 'Obras menores y Pequeñas' },
  { id: 11, name: 'Obras Hidráulicas' },
  { id: 12, name: 'Materiales de Construcción' },
  { id: 13, name: 'Maquinarias y Herramientas' },
  { id: 14, name: 'Otras Obras' },
]

export default connect(mapStateToProps, mapDispatchToProps)(FilterQuotation)

